import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getHTML } from '../../../services/commonFunction';
import { ArrowRight, Zap, ChevronRight, Eye } from 'lucide-react';

function ServicesCard({ data }) {
  const contentData = useSelector(state => state.Data.contentData);
  const langPrefix = useSelector(state => state.Data.langPrefix);
  const [isHovered, setIsHovered] = useState(false);

  // Clean up HTML description
  const cleanDescription = getHTML(data?.translate?.description)
    ?.replaceAll("&nbsp;", " ")
    ?.replaceAll("&nbsp", " ")
    ?.substring(0, 120);

  return (
    <Link 
      to={`/${langPrefix}service/${data?.path}`}
      className="group relative block overflow-hidden rounded-xl bg-white shadow-lg transition-all duration-500 hover:shadow-2xl h-[320px] md:h-[360px] lg:h-[400px]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src={data?.image_full_url} 
          alt={data?.translate?.title}
          className={`w-full h-full object-cover transition-transform duration-700 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent opacity-90"></div>
        
        {/* Additional Gradient on Hover */}
        <div className={`absolute inset-0 bg-gradient-to-t from-blue-900/70 via-blue-800/40 to-transparent transition-opacity duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}></div>
      </div>

      {/* Electrical Badge */}
      <div className="absolute top-4 right-4 z-10">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
          <Zap className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 h-full flex flex-col justify-end p-6">
        {/* Title */}
        <h3 className="text-white text-2xl md:text-3xl font-bold mb-3 leading-tight group-hover:text-blue-100 transition-colors duration-300">
          {data?.translate?.title}
        </h3>
        
        {/* Decorative Line */}
        <div className={`h-1 bg-gradient-to-r from-blue-400 to-blue-500 mb-4 transition-all duration-500 ${
          isHovered ? 'w-16' : 'w-8'
        }`}></div>
        
        {/* Description - Slides up on hover */}
        <div className={`overflow-hidden transition-all duration-500 ${
          isHovered ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <p className="text-blue-100 text-sm md:text-base font-light leading-relaxed">
            {cleanDescription}
            {cleanDescription?.length >= 120 && '...'}
          </p>
        </div>

        {/* Read More Button - Appears on hover */}
        <div className={`mt-4 transition-all duration-500 ${
          isHovered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}>
          <div className="inline-flex items-center space-x-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 group/btn">
            <span>{contentData?.read_more || 'Ətraflı'}</span>
            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Bottom Info Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500"></div>
      </div>

      {/* Floating Icon on Hover */}
      <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${
        isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
      }`}>
        <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
          <Eye className="w-8 h-8 text-white/70" />
        </div>
      </div>

      {/* Corner Decoration */}
      <div className="absolute top-0 left-0 w-20 h-20">
        <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-blue-400/30 rounded-tl-xl"></div>
      </div>
      <div className="absolute bottom-0 right-0 w-20 h-20">
        <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-blue-400/30 rounded-br-xl"></div>
      </div>
    </Link>
  );
}

export default ServicesCard;